import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import "./style.css";
import "./datepicker.css";
import { Row, Col, Typography, Tabs, Button, DatePicker, Drawer, message, Popconfirm, Dropdown, Menu } from "antd";
import { AppContext } from "../../context/AppContext";
import { PROCURING_ENTITY_REPORTS_ENDPOINT } from "../../API/endpoints";
import API from "../../API";
import reportsAPI from "../../API/reports";
import procuringEntitiesAPI from "../../API/procuring_entities";
import CustomList from "../components/List";
import ListItem from "../components/ListItem";
import CustomPagination from "../components/CustomPagination";
import { DownloadOutlined, EyeOutlined, ReloadOutlined, MessageOutlined } from "@ant-design/icons";
import ReportComments from "./components/ReportComments";
import { handleResizeObserverError } from "../../Util/resizeObserver";
import { usePCTReport, usePMCReport, usePITReport, useGeneralRecommendations } from "../../hooks/useDataHooks";
import { generateDateString, isoDateToHumanReadableDate } from "../../Util";
import * as AppPermissions from "../../Util/permissions";
import Loader from "../Packages/componets/Loader";
import Icon from "@ant-design/icons";
import { ReactComponent as ReportsSvg } from "../../assets/icons/reports.svg";
import CSCContractInfo from ".";
import HumanResources from "./components/CSCStaffMobilization";
import CscFinancialStatus from "./components/CscFinancialStatus";
import { usePCTDateFilter } from "../../hooks/usePCTDateFilter";
import { usePITDateFilter } from "../../hooks/usePITDateFilter";
import moment from "moment";

const { TabPane } = Tabs;

const tabStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 500,
};

const iconStyle = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "8px",
  fontWeight: "700",
  color: "white",
};

const useColumnLayouts = (activeTab) => {
  return useMemo(() => {
    const baseLayout = { xxl: 4, xl: 4, lg: 4, md: 4, sm: 6, xs: 6 };
    const largeLayout = { xxl: 6, xl: 6, lg: 6, md: 4, sm: 4, xs: 4 };

    return {
      reportedByLayout: baseLayout,
      workingSectionLayout: largeLayout,
      reportedDateLayout: { xxl: 6, xl: 6, lg: 6, md: 6, sm: 4, xs: 4 },
      actionLayout: largeLayout
    };
  }, [activeTab]);
};



const ProjectActivities = ({ match }) => {
  const {
    params: {  procuringEntityId },
  } = match;


  const [activeTab, setActiveTab] = useState("CSC");
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState("Executive Summary");
  const [report, setReport] = useState([]);
  const { selectedReportID, permissions } = useContext(AppContext);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const [pctCurrentPage, setPctCurrentPage] = useState(1);
  const [pctPageSize, setPctPageSize] = useState(15);
  const [pmcCurrentPage, setPmcCurrentPage] = useState(1);
  const [pmcPageSize, setPmcPageSize] = useState(15);
  const [pitCurrentPage, setPitCurrentPage] = useState(1);
  const [pitPageSize, setPitPageSize] = useState(15);

  const [reprocessingSubmissionIds, setReprocessingSubmissionIds] = useState(new Set());
  const [commentsOpen, setCommentsOpen] = useState(false);

  useEffect(() => {
    handleResizeObserverError();
  }, []);

  const canReprocessSubmissions = permissions?.includes(AppPermissions.CAN_REPROCESS_SUBMISSION);

  const markReprocessingIds = useCallback((ids) => {
    setReprocessingSubmissionIds((prev) => {
      const updated = new Set(prev);
      ids.forEach((id) => updated.add(String(id)));
      return updated;
    });
  }, []);

  const unmarkReprocessingIds = useCallback((ids) => {
    setReprocessingSubmissionIds((prev) => {
      const updated = new Set(prev);
      ids.forEach((id) => updated.delete(String(id)));
      return updated;
    });
  }, []);

  const isReprocessing = useCallback(
    (id) => {
      if (id === undefined || id === null || id === "") {
        return false;
      }
      return reprocessingSubmissionIds.has(String(id));
    },
    [reprocessingSubmissionIds]
  );

  const resolveSubmissionId = useCallback((record) => {
    if (!record) {
      return null;
    }

    return (
      record.payload_id ??
      record.submission_id ??
      record.webhook_id ??
      record.webhookId ??
      record.payloadId ??
      record.webhook?.id ??
      record.submission?.id ??
      null
    );
  }, []);

  const handleReprocessSubmission = useCallback(
    async (submissionId) => {
      const ids = Array.isArray(submissionId) ? submissionId : [submissionId];
      const sanitizedIds = ids.filter((id) => id !== undefined && id !== null && id !== "");

      if (!sanitizedIds.length) {
        message.error("Submission identifier is missing; cannot reprocess this report yet.");
        return;
      }

      markReprocessingIds(sanitizedIds);

      try {
        const response = await reportsAPI.reprocessSubmissions(sanitizedIds);
        const successMessage = response?.message || "Submission queued for reprocessing.";
        message.success(successMessage);
      } catch (error) {
        console.error("Error reprocessing submission:", error);
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error.message ||
          "Failed to queue submission for reprocessing.";
        message.error(errorMessage);
      } finally {
        unmarkReprocessingIds(sanitizedIds);
      }
    },
    [markReprocessingIds, unmarkReprocessingIds]
  );

  const renderReprocessButton = useCallback(
    (submissionId, size = "middle") => {
      if (!canReprocessSubmissions) {
        return null;
      }

      const disabled = !submissionId;
      const loading = submissionId ? isReprocessing(submissionId) : false;

      return (
        <Popconfirm
          title="Reprocess submission"
          description="Mark this submission as unprocessed so it will be retried shortly."
          onConfirm={() => handleReprocessSubmission(submissionId)}
          okText="Yes"
          cancelText="No"
          disabled={disabled}
        >
          <Button
            className="prst-button"
            shape="default"
            icon={<ReloadOutlined />}
            size={size}
            loading={loading}
            disabled={loading || disabled}
            title={
              disabled
                ? "Submission identifier unavailable for this report."
                : "Mark this submission as unprocessed for the next run."
            }
          >
            <span className="hide-on-mobile">Reprocess</span>
          </Button>
        </Popconfirm>
      );
    },
    [canReprocessSubmissions, handleReprocessSubmission, isReprocessing]
  );

  const { reportedByLayout, workingSectionLayout, reportedDateLayout, actionLayout } =
    useColumnLayouts(activeTab);

  const {
    startDate: pctStartDate,
    endDate: pctEndDate,
    updateDateRange: updatePCTDateRange,
    clearDateRange: clearPCTDateRange,
  } = usePCTDateFilter();
  
  const {
    startDate: pitStartDate,
    endDate: pitEndDate,
    updateDateRange: updatePITDateRange,
    clearDateRange: clearPITDateRange,
  } = usePITDateFilter();

  const { pitReport, pagination: pitPagination, loading: pitLoading, error: pitError } = usePITReport(procuringEntityId, pitStartDate, pitEndDate, pitCurrentPage, pitPageSize);
  const { pctReport, pagination: pctPagination, loading: pctLoading, error: pctError } = usePCTReport(procuringEntityId, pctStartDate, pctEndDate, pctCurrentPage, pctPageSize);
  const { pmcReport, pagination: pmcPagination, loading: pmcLoading, error: pmcError } = usePMCReport(procuringEntityId, pctStartDate, pctEndDate, pmcCurrentPage, pmcPageSize);



  const filteredPitReports = useMemo(() => {
    return Array.isArray(pitReport) ? pitReport : [];
  }, [pitReport]);

  const cscSubmissionId = useMemo(() => resolveSubmissionId(report), [report, resolveSubmissionId]);

  const { recommendations } = useGeneralRecommendations(selectedReportID);

  const getColumns = () => {
    return [
      { ...workingSectionLayout, header: "Working Section" },
      { ...reportedByLayout, header: "Reported By" },
      { ...reportedDateLayout, header: "Reported Date" },
      { ...actionLayout, header: "Actions" },
    ];
  };

  const { Title, Paragraph } = Typography;


  const getReports = useCallback(async () => {
    if (!selectedReportID) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await API.get(
        `${PROCURING_ENTITY_REPORTS_ENDPOINT}/${selectedReportID}`
      );
      setReport(response);
    } catch (error) {
      console.error("Error fetching reports:", error);
      setReport([]);
    } finally {
      setLoading(false);
    }
  }, [selectedReportID]);

  useEffect(() => {
    getReports();
  }, [getReports]);

  const handlePMCDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      updatePCTDateRange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
    } else {
      clearPCTDateRange();
    }
  };

  const handlePCTDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      updatePCTDateRange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
    } else {
      clearPCTDateRange();
    }
  };

  const handlePITDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      updatePITDateRange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
    } else {
      clearPITDateRange();
    }
  };

  const downloadReport = (url) => {
    if (!url) {
      message.error("No report found");
      return;
    }
    window.open(url, "_blank");
  };

  const downloadPCTReport = async (reportId) => {
    if (!reportId) {
      message.error("No report ID found");
      return;
    }

    try {
      await reportsAPI.downloadPCTReport(reportId);
      message.success("Report downloaded successfully");
    } catch (error) {
      console.error("Error downloading PCT report:", error);
      message.error("Failed to download report");
    }
  };

  const downloadESHReport = async (procuringEntityId) => {
    if (!procuringEntityId) {
      message.error("No procuring entity ID found");
      return;
    }
    
    try {
      const response = await procuringEntitiesAPI.downloadESHReport(procuringEntityId);
      const { data } = response;
      
      if (data && data.esh_pdf_report) {
        message.success("ESH report opened successfully");
      } else {
        message.warning("No ESH report available for this procuring entity");
      }
    } catch (error) {
      console.error("Error downloading ESH report:", error);
      if (error.response && error.response.status === 404) {
        message.warning("No ESH report available for this procuring entity");
      } else {
        message.error("Failed to download ESH report");
      }
    }
  };

  const handleViewClick = (item) => {
    setActiveItem(item);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setActiveItem(null);
  };

  const handlePctPageChange = (page, size) => {
    setPctCurrentPage(page);
    setPctPageSize(size);
  };

  const handlePmcPageChange = (page, size) => {
    setPmcCurrentPage(page);
    setPmcPageSize(size);
  };

  const handlePitPageChange = (page, size) => {
    setPitCurrentPage(page);
    setPitPageSize(size);
  };

  const getDrawerTitle = () => {
    if (!activeItem) return "Report Details";

    switch (activeTab) {
      case "PMC":
        return `PMC Report - ${activeItem.section || "Unknown Section"}`;
      case "PCT":
        return `PCT Report - ${activeItem.section || "Unknown Section"}`;
      case "PIT":
        return `PIT Report - ${activeItem.section || "Unknown Section"}`;
      default:
        return "Report Details";
    }
  };

  if (loading || pitLoading || pctLoading || pmcLoading) {
    return <Loader fontSize={50} />;
  }

  const ReportsIcon = () => (
    <Icon
      style={{ ...iconStyle }}
      component={ReportsSvg}
      className="navmenu-icon"
    />
  );

  return (
    <>
      <div
        style={{
          paddingTop: "20px",
          backgroundColor: "#f8f9fa",
          marginBottom: "40px",
        }}
        className="prst-tabs-container"
      >
        <Row
          gutter={16}
          align="middle"
          style={{ width: "100%", marginBottom: "16px" }}
        >
          <Col span={24}>
            <Title level={3} style={{ color: "#0f6788", marginBottom: "0" }}>
              Progress Reports
            </Title>
          </Col>
        </Row>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ minHeight: "100vh" }}
          className="prst-tabs"
        >
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "CSC" ? "#000000" : undefined,
                }}
              >
                <span style={{ ...iconStyle, backgroundColor: "#6B46C1" }}>
                  CSC
                </span>
                CSC Report
              </span>
            }
            key="CSC"
            style={{
              backgroundColor: activeTab === "CSC" ? "#fff" : "#f8f9fa",
            }}
          >
            <div className="csc-rport-body">
              <div
                style={{
                  padding: "4px 5px",
                  backgroundColor: "#f8f9fa",
                  marginInline: "-20px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "20px 0px",
                  borderRadius: "7px"
                }}
              >
                <div>
                  <h2 className="report-section-title">
                    Reporting Period: {isoDateToHumanReadableDate(report?.start) + " - " + isoDateToHumanReadableDate(report?.end)}
                  </h2>
                  <h4 className="report-section-sub-title">
                    Complete CSC report with detailed analysis
                    and appendices
                  </h4>
                </div>
                <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                  <Dropdown
                    overlay={(
                      <Menu>
                        <Menu.Item key="csc" onClick={() => downloadReport(report?.document_url)}>
                          CSC Report
                        </Menu.Item>
                        <Menu.Item key="esh" onClick={() => downloadESHReport(procuringEntityId)}>
                          ESH Report
                        </Menu.Item>
                      </Menu>
                    )}
                  >
                    <Button icon={<DownloadOutlined />} className="prst-button">
                      Downloads
                    </Button>
                  </Dropdown>
                  {renderReprocessButton(cscSubmissionId)}
                  <Button
                    icon={<MessageOutlined />}
                    className="prst-button"
                    onClick={() => setCommentsOpen(true)}
                  >
                    Comments
                  </Button>
                </div>
              </div>
              <div className="csc-navigation-buttons" style={{ marginBottom: "10px" }}>
                <Button type="outline" shape="round" style={{ marginRight: "10px" }} size="middle" className={active === "Executive Summary" ? 'prst-button-secondary' : 'prst-button-mute'} onClick={() => setActive("Executive Summary")}>
                  Executive Summary
                </Button>
                <Button type="outline" shape="round" style={{ marginRight: "10px" }} size="middle" className={active === "CSC Activities" ? 'prst-button-secondary' : 'prst-button-mute'} onClick={() => setActive("CSC Activities")}>
                  CSC Activities
                </Button>
                <Button type="outline" shape="round" style={{ marginRight: "10px" }} size="middle" className={active === "Contract Information" ? 'prst-button-secondary' : 'prst-button-mute'} onClick={() => setActive("Contract Information")}>
                  Contract Information
                </Button>
                <Button type="outline" shape="round" style={{ marginRight: "10px" }} size="middle" className={active === "Financial Status" ? 'prst-button-secondary' : 'prst-button-mute'} onClick={() => setActive("Financial Status")}>
                  Financial Status
                </Button>
                <Button type="outline" shape="round" style={{ marginRight: "10px" }} size="middle" className={active === "Staffs Mobilization" ? 'prst-button-secondary' : 'prst-button-mute'} onClick={() => setActive("Staffs Mobilization")}>
                  Staffs Mobilization
                </Button>
                <Button type="outline" shape="round" style={{ marginRight: "10px" }} size="middle" className={active === "General Recommendation" ? 'prst-button-secondary' : 'prst-button-mute'} onClick={() => setActive("General Recommendation")}>
                  General Recommendation
                </Button>
              </div>

              {(active === "Executive Summary") && (
                <>
                  {!selectedReportID ?
                    (
                      <div className="report-empty-state">
                        <ReportsIcon className="report-icon" />
                        <Paragraph>
                          Please select a report to view its details.
                        </Paragraph>
                      </div>
                    ) :
                    (
                      <>
                        <Title level={5}>Project Overview</Title>
                        <Paragraph style={{ marginBottom: "20px" }}>
                          {report?.summary}
                        </Paragraph>
                        <Paragraph>
                          {report?.ongoing_packages_overview}
                        </Paragraph>

                        <Title level={5}>Key Achievements</Title>
                        <Paragraph style={{ marginBottom: "20px" }}>
                          {report?.activities_summary}
                        </Paragraph>
                      </>
                    )}
                </>
              )}
              {(active === "CSC Activities") && (
                <>
                  {!selectedReportID ?
                    (
                      <div className="report-empty-state">
                        <ReportsIcon className="report-icon" />
                        <Paragraph>
                          Please select a report to view its details.
                        </Paragraph>
                      </div>
                    ) :
                    (<>
                      <Title level={5}>Summary of CSC Role and Activities During the Reporting Period</Title>
                      <Paragraph style={{ marginBottom: "20px" }}>
                        {report?.activities_summary}
                      </Paragraph>
                    </>)}
                </>
              )}
              {active === 'Contract Information' && <CSCContractInfo procuringEntityId={procuringEntityId} />}
              {active === 'Financial Status' && <CscFinancialStatus procuringEntityId={procuringEntityId} />}
              {active === 'Staffs Mobilization' && <HumanResources procuringEntityId={procuringEntityId} />}
              {(active === "General Recommendation") && (
                <>
                  {!selectedReportID ?
                    (
                      <div className="report-empty-state">
                        <ReportsIcon className="report-icon" />
                        <Paragraph>
                          Please select a report to view its details.
                        </Paragraph>
                      </div>
                    ) :
                    (<>
                      <Title level={5}>General Recommendations</Title>
                      <ul>
                        {recommendations && recommendations.length > 0 ? (
                          recommendations.map((item, index) => (
                            <li key={index} style={{ marginBottom: "25px" }}>
                              {item?.recommendation}
                            </li>
                          ))
                        ) : (
                          <li className="report-empty-state">
                            No recommendations available
                          </li>
                        )}
                      </ul>
                    </>)}
                </>
              )}
            </div>
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "PMC" ? "#000000" : undefined,
                }}
              >
                <span style={{ ...iconStyle, backgroundColor: "#36e4ed" }}>
                  PMC
                </span>
                PMC Report
              </span>
            }
            key="PMC"
          >
            <div
              className="pmc-report-header"
              style={{
                padding: "20px",
                backgroundColor: "#f8f9fa",
                marginInline: "-20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "20px 0",
              }}
            >
              <div>
                <h2 className="report-section-title">
                  PMC Reports
                </h2>
                <h4 className="report-section-sub-title">
                  PMC report with detailed analysis
                  and appendices
                </h4>
              </div>
              <div style={{ minWidth: "300px" }}>
                <DatePicker.RangePicker
                  value={pctStartDate && pctEndDate ? [moment(pctStartDate), moment(pctEndDate)] : null}
                  onChange={handlePMCDateChange}
                  placeholder={["Start Date", "End Date"]}
                  size="middle"
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <CustomList
              itemName="PMC Report"
              items={Array.isArray(pmcReport) ? pmcReport : []}
              page={pmcCurrentPage}
              itemCount={pmcPagination?.total || 0}
              loading={pmcLoading}
              headerLayout={getColumns()}
              renderListItem={({ item }) => {
                const submissionId = resolveSubmissionId(item);

                return (
                  <ListItem
                    key={item.id}
                    name={item.section || "-"}
                    item={item}
                    isPackage={false}
                    style={{ marginBottom: 12, cursor: "pointer" }}
                    renderActions={() => false}
                  >
                    <Col {...workingSectionLayout} className="contentEllipse" data-label="Section">
                      {item.section}
                    </Col>
                    <Col {...reportedByLayout} className="contentEllipse" data-label="Reported By">
                      {item.staff_name}
                    </Col>
                    <Col {...reportedDateLayout} className="contentEllipse-" data-label="Visit Date">
                      {generateDateString(item.visit_date)}
                    </Col>
                    <Col
                      {...actionLayout}
                      className="contentEllipse actions-col"
                      style={{ display: "flex", gap: "10px", justifyContent: "flex-end", flexWrap: "wrap", minWidth: "240px" }}
                    >
                      <Button className="prst-button" shape="default" icon={<DownloadOutlined />} size="middle" onClick={() => downloadPCTReport(item.id)}>
                        <span className="hide-on-mobile">Download</span>
                      </Button>
                      <Button className="prst-button-mute" variant="solid" shape="default" icon={<EyeOutlined />} size="middle" onClick={() => handleViewClick(item)}>
                        <span className="hide-on-mobile">View</span>
                      </Button>
                      {renderReprocessButton(submissionId)}
                    </Col>
                  </ListItem>
                );
              }}
              showToolbar={false}
            />
            <CustomPagination 
              pagination={pmcPagination}
              onPageChange={handlePmcPageChange}
              loading={pmcLoading}
            />
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "PCT" ? "#000000" : undefined,
                }}
              >
                <span style={{ ...iconStyle, backgroundColor: "#ED8936" }}>
                  PCT
                </span>
                PCT Report
              </span>
            }
            key="PCT"
          >
            <div
              className="pct-report-header"
              style={{
                padding: "20px",
                backgroundColor: "#f8f9fa",
                marginInline: "-20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "20px 0",
              }}
            >
              <div>
                <h2 className="report-section-title">
                  PCT Reports
                </h2>
                <h4 className="report-section-sub-title">
                  PCT report with detailed analysis
                  and appendices
                </h4>
              </div>
              <div style={{ minWidth: "300px" }}>
                <DatePicker.RangePicker
                  value={pctStartDate && pctEndDate ? [moment(pctStartDate), moment(pctEndDate)] : null}
                  onChange={handlePCTDateChange}
                  placeholder={["Start Date", "End Date"]}
                  size="middle"
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <CustomList
              itemName="PCT Report"
              items={Array.isArray(pctReport) ? pctReport : []}
              page={pctCurrentPage}
              itemCount={pctPagination?.total || 0}
              loading={pctLoading}
              headerLayout={getColumns()}
              renderListItem={({ item }) => {
                const submissionId = resolveSubmissionId(item);

                return (
                  <ListItem
                    key={item.id}
                    name={item.section || "-"}
                    item={item}
                    isPackage={false}
                    style={{ marginBottom: 12, cursor: "pointer" }}
                    renderActions={() => false}
                  >
                    <Col {...workingSectionLayout} className="contentEllipse" data-label="Section">
                      {item.section}
                    </Col>
                    <Col {...reportedByLayout} className="contentEllipse" data-label="Reported By">
                      {item.staff_name}
                    </Col>
                    <Col {...reportedDateLayout} className="contentEllipse-" data-label="Visit Date">
                      {generateDateString(item.visit_date)}
                    </Col>
                    <Col
                      {...actionLayout}
                      className="contentEllipse actions-col"
                      style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "flex-end", minWidth: "240px" }}
                    >
                      <Button className="prst-button" shape="default" icon={<DownloadOutlined />} size="middle" onClick={() => downloadPCTReport(item.id)}>
                        <span className="hide-on-mobile">Download</span>
                      </Button>
                      <Button className="prst-button-mute" variant="solid" shape="default" icon={<EyeOutlined />} size="middle" onClick={() => handleViewClick(item)}>
                        <span className="hide-on-mobile">View</span>
                      </Button>
                      {renderReprocessButton(submissionId)}
                    </Col>
                  </ListItem>
                );
              }}
              showToolbar={false}
            />
            <CustomPagination 
              pagination={pctPagination}
              onPageChange={handlePctPageChange}
              loading={pctLoading}
            />
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  ...tabStyle,
                  color: activeTab === "PIT" ? "#000000" : undefined,
                }}
              >
                <span style={{ ...iconStyle, backgroundColor: "#38A169" }}>
                  PIT
                </span>
                PIT Report
              </span>
            }
            key="PIT"
          >
            <div
              className="pit-report-header"
              style={{
                padding: "20px",
                backgroundColor: "#f8f9fa",
                marginInline: "-20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "20px 0",
              }}
            >
              <div>
                <h2 className="report-section-title">
                  PIT Report
                </h2>
                <h4 className="report-section-sub-title">
                  PIT report with detailed analysis
                </h4>
              </div>
              <div style={{ minWidth: "300px" }}>
                <DatePicker.RangePicker
                  value={pitStartDate && pitEndDate ? [moment(pitStartDate), moment(pitEndDate)] : null}
                  onChange={handlePITDateChange}
                  placeholder={["Start Date", "End Date"]}
                  size="middle"
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <CustomList
              itemName="PIT Report"
              items={filteredPitReports}
              page={pitCurrentPage}
              itemCount={pitPagination?.total || 0}
              loading={pitLoading}
              headerLayout={getColumns()}
              renderListItem={({ item }) => {
                const submissionId = resolveSubmissionId(item);

                return (
                  <ListItem
                    key={item.id}
                    name={item.section || "-"}
                    item={item}
                    isPackage={false}
                    style={{ marginBottom: 12, cursor: "pointer" }}
                  >
                    <Col {...workingSectionLayout} className="contentEllipse" data-label="Section">
                      {item.section || "-"}
                    </Col>
                    <Col {...reportedByLayout} className="contentEllipse" data-label="Reported By">
                      {item.staff_name || "-"}
                    </Col>
                    <Col {...reportedDateLayout} className="contentEllipse-" data-label="Visit Date">
                      {generateDateString(item.visit_date) || "-"}
                    </Col>
                    <Col
                      {...actionLayout}
                      className="contentEllipse actions-col"
                      style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                    >
                      <Button className="prst-button" shape="default" icon={<DownloadOutlined />} size="middle" onClick={() => downloadReport(item.pit_report)}>
                        <span className="hide-on-mobile">Download</span>
                      </Button>
                      <Button className="prst-button-mute" variant="solid" shape="default" icon={<EyeOutlined />} size="middle" onClick={() => handleViewClick(item)}>
                        <span className="hide-on-mobile">View</span>
                      </Button>
                      {renderReprocessButton(submissionId)}
                    </Col>
                  </ListItem>
                );
              }}
              showToolbar={false}
            />
            <CustomPagination
              pagination={pitPagination}
              onPageChange={handlePitPageChange}
              loading={pitLoading}
            />
          </TabPane>

        </Tabs>
      </div>

      <Drawer
        title={getDrawerTitle()}
        placement="right"
        onClose={handleCloseDrawer}
        visible={drawerVisible}
        width={600}
        style={{ zIndex: 1000 }}
        destroyOnClose={false}
        bodyStyle={{ padding: "24px" }}
        headerStyle={{ backgroundColor: "#f0f0f0" }}
      >
        {activeItem && (
          <div>
            <div style={{ marginBottom: "20px" }}>
              <h3 style={{ color: "#0f6788", marginBottom: "16px" }}>Report Information</h3>

              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "4px" }}>
                <p><strong>Section:</strong> {activeItem.section || "-"}</p>
                <p><strong>Reported By:</strong> {activeItem.staff_name || "-"}</p>
                <p><strong>Visit Date:</strong> {generateDateString(activeItem.visit_date) || "-"}</p>
                {activeItem.exit_date && (
                  <p><strong>Exit Date:</strong> {generateDateString(activeItem.exit_date)}</p>
                )}
                {activeItem.email && (
                  <p><strong>Email:</strong> {activeItem.email}</p>
                )}
                {activeItem.user?.email && (
                  <p><strong>Email:</strong> {activeItem.user.email}</p>
                )}
                {activeItem.pct_report?.section && (
                  <p><strong>Work Section:</strong> {activeItem.pct_report.section}</p>
                )}
              </div>

            </div>

            {(activeTab === "PMC" || activeTab === "PCT") && (
              <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "grid", gap: "16px" }}>
                  <div>
                    <h3 style={{ marginBottom: "12px" }}><strong>Visit Information</strong></h3>
                    <div style={{ display: "grid", gap: "8px" }}>
                      <p><strong>Staff Name:</strong> {activeItem.staff_name || "--//--"}</p>
                      <p><strong>Section:</strong> {activeItem.section || "--//--"}</p>
                      <p><strong>Visit Date:</strong> {new Date(activeItem.visit_date).toLocaleDateString()}</p>
                      <p><strong>Exit Date:</strong> {new Date(activeItem.exit_date).toLocaleDateString()}</p>
                      <p><strong>Purpose of Visit:</strong> {activeItem.purpose_of_visit || "--//--"}</p>
                    </div>
                  </div>

                  <div>
                    <h3 style={{ marginBottom: "12px" }}><strong>Activities & Observations</strong></h3>
                    <div style={{ display: "grid", gap: "8px" }}>
                      <p><strong>Activities Carried:</strong> {activeItem.activities_carried || "--//--"}</p>
                      <p><strong>Companion:</strong> {activeItem.companion || "--//--"}</p>
                      <p><strong>Observations:</strong> {activeItem.observations || "--//--"}</p>
                    </div>
                  </div>

                  <div>
                    <h3 style={{ marginBottom: "12px" }}><strong>Sub Projects Visited</strong></h3>
                    <div>
                      {activeItem?.sub_projects_visited?.length > 0 ? (
                        <ul style={{ paddingLeft: "20px", margin: 0 }}>
                          {activeItem.sub_projects_visited.map((sub_project, index) => (
                            <li key={index}>{sub_project?.sub_project?.name}</li>
                          ))}
                        </ul>
                      ) : (
                        <p>No sub-projects visited</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 style={{ marginBottom: "12px" }}><strong>Challenges</strong></h3>
                    <div>
                      {activeItem?.challenges?.length > 0 ? (
                        <ul style={{ paddingLeft: "20px", margin: 0 }}>
                          {activeItem.challenges?.slice(0, 3).map((challenge, index) => (
                            <li key={index}>
                              <p><strong>Description:</strong> {challenge.challenge_description}</p>
                              {challenge.action_taken && (
                                <p><strong>Action Taken:</strong> {challenge.action_taken}</p>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>No challenges reported</p>
                      )}
                    </div>
                  </div>

                  {activeItem.key_people_doc_url && (
                    <div>
                      <p>
                        <strong>Key People Document:</strong>
                        <Button
                          type="link"
                          onClick={() => window.open(activeItem.key_people_doc_url, '_blank')}
                          style={{ padding: "0 8px", height: "auto" }}
                        >
                          View Document
                        </Button>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "PIT" && (
              <div style={{ marginBottom: "20px" }}>

                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "4px" }}>
                  <p><strong>General Observation:</strong> {activeItem?.general_observations || "-"}</p>
                </div>
                {activeItem.pit_report && (
                  <p><strong>Report File:</strong>
                    <Button
                      type="link"
                      onClick={() => downloadReport(activeItem.pit_report)}
                      style={{ padding: 0, height: "auto" }}
                    >
                      View Report
                    </Button>
                  </p>
                )}
              </div>
            )}

          </div>
        )}
      </Drawer>

      <ReportComments
        visible={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        reportId={report?.id}
      />
    </>
  );
};

export default ProjectActivities;
